import { ArrowLeft20, PlayOutline20 } from '@carbon/icons-react';
import { Skeleton } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { CpWidgetVariantType } from 'api/customer-portal/types';
import { CpSectionWrapper } from 'components/organisms/customer-portal/widgets/common';
import {
  ControlButton,
  ControlsAtBottom,
  RightArrow,
} from 'components/organisms/customer-portal/widgets/image-gallery/variants/image-gallery-carousel';
import {
  VideoScrollableMenuWrapper,
  VideoScrollableMenuSectionWrapper,
  VideoScrollableMenuSelectedVideoWrapper,
  VideoScrollableMenuVideoList,
  VideoScrollableMenuVideoListItem,
} from 'components/organisms/customer-portal/widgets/video-list/variants/video-list-scrollable-side-menu';
import {
  VideoDetails,
  VideoSection,
} from 'components/organisms/customer-portal/widgets/video-list/variants/video-list-single-video';
import {
  VideoListVerticalVideoStackWrapper,
  VideoListVerticalVideoStackWrapperVideoCard,
  VideoListVerticalVideoStackWrapperVideoDetails,
} from 'components/organisms/customer-portal/widgets/video-list/variants/video-list-vertical-video-stack';
import {
  CpVideoCarouselWrapper,
  VideoCarouselVideoCard,
  VideoCarouselVideosGrid,
} from 'components/organisms/customer-portal/widgets/video-list/variants/video-list-video-grid';
import { useIsResponsive } from 'hooks/use-media-query';
import { tryGetTextFromHTML } from 'utils/getTextFromHTML';
import { CpLoadingProps, TitleAndDesc } from '../common';

const VideoTitle = styled(Skeleton.Button)`
  .ant-skeleton,
  &.ant-skeleton,
  & {
    max-width: 100% !important;
    width: 150px !important;
    height: 30px !important;
  }

  .ant-skeleton-button,
  &.ant-skeleton-button {
    width: 100% !important;
    height: 100% !important;
  }
`;

const VideoDescription = styled(Skeleton.Button)`
  .ant-skeleton,
  &.ant-skeleton,
  & {
    max-width: 100% !important;
    width: 400px !important;
    height: 76px !important;
  }

  .ant-skeleton-button,
  &.ant-skeleton-button {
    width: 100% !important;
    height: 100% !important;
  }
`;

const VideoPlayer = styled(Skeleton.Image)`
  .ant-skeleton,
  &.ant-skeleton,
  & {
    max-width: 100% !important;
    width: 640px !important;
    height: 360px !important;
  }

  .ant-skeleton-image,
  &.ant-skeleton-image {
    width: 100% !important;
    height: 100% !important;
  }
`;

const SmallVideoPlayer = styled(Skeleton.Image)`
  .ant-skeleton,
  &.ant-skeleton,
  & {
    max-width: 100% !important;
    min-width: 294px !important;
    min-height: 165px !important;
    width: 100% !important;
    height: 100% !important;
  }

  .ant-skeleton-image,
  &.ant-skeleton-image {
    width: 100% !important;
    height: 100% !important;
  }
`;

const Vc = VideoListVerticalVideoStackWrapperVideoCard;

const StyledVideoListVerticalVideoStackWrapperVideoCard = styled(Vc)`
  width: 349px !important;
  height: 443px !important;
`;

const StyledVideoCarouselVideoCard = styled(VideoCarouselVideoCard)`
  width: 349px !important;
  height: 443px !important;
`;

const Content: React.FC<{
  input: Record<string, unknown>;
  variantType: string;
}> = ({ input, variantType }) => {
  const isResponsive = useIsResponsive();

  switch (variantType) {
    case CpWidgetVariantType.SingleVideo:
      return (
        <VideoSection>
          <VideoDetails>
            <VideoTitle active />
            <VideoDescription active />
          </VideoDetails>

          <VideoPlayer />
        </VideoSection>
      );

    case CpWidgetVariantType.VerticalVideoStack:
      return (
        <VideoListVerticalVideoStackWrapper>
          {((input?.resources as unknown[]) ?? [1, 2, 3, 4, 5])?.map(() => (
            <VideoListVerticalVideoStackWrapperVideoCard>
              <div className="videoPlayer">
                <SmallVideoPlayer />
              </div>

              <VideoListVerticalVideoStackWrapperVideoDetails>
                <VideoTitle active />
                <VideoDescription active />
              </VideoListVerticalVideoStackWrapperVideoDetails>
            </VideoListVerticalVideoStackWrapperVideoCard>
          ))}
        </VideoListVerticalVideoStackWrapper>
      );

    case CpWidgetVariantType.VideoListGrid:
      return (
        <CpVideoCarouselWrapper>
          {isResponsive ? (
            <>
              <Swiper
                pagination={{ clickable: true }}
                className="mySwiper"
                speed={500}
                spaceBetween={50}
                autoplay={{
                  delay: 2000,
                  disableOnInteraction: true,
                }}
              >
                {((input?.resources as unknown[]) ?? [1, 2, 3, 4, 5])?.map(
                  () => (
                    <SwiperSlide>
                      <StyledVideoListVerticalVideoStackWrapperVideoCard>
                        <SmallVideoPlayer />
                        <VideoTitle active />
                        <VideoDescription active />
                      </StyledVideoListVerticalVideoStackWrapperVideoCard>
                    </SwiperSlide>
                  ),
                )}
              </Swiper>
              <ControlsAtBottom>
                <ControlButton
                  className="controlButtonMinimized"
                  icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
                />
                <ControlButton
                  className="controlButtonMinimized"
                  icon={<RightArrow className="cp-svg-auto-adjust-fill" />}
                />
              </ControlsAtBottom>
            </>
          ) : (
            <VideoCarouselVideosGrid>
              {((input?.resources as unknown[]) ?? [1, 2, 3, 4, 5])?.map(() => (
                <StyledVideoCarouselVideoCard>
                  <SmallVideoPlayer />
                  <VideoTitle active />
                  <VideoDescription active />
                </StyledVideoCarouselVideoCard>
              ))}
            </VideoCarouselVideosGrid>
          )}
        </CpVideoCarouselWrapper>
      );

    case CpWidgetVariantType.ScrollableSideMenu:
      return (
        <VideoScrollableMenuWrapper>
          <VideoScrollableMenuSectionWrapper>
            <VideoScrollableMenuSelectedVideoWrapper>
              <VideoPlayer />
            </VideoScrollableMenuSelectedVideoWrapper>

            <VideoScrollableMenuVideoList>
              {((input?.resources as unknown[]) ?? [1, 2, 3, 4, 5]).map(
                (video, index) => (
                  <VideoScrollableMenuVideoListItem isActive={index === 1}>
                    {index === 1 ? (
                      <>
                        <PlayOutline20 />
                        <VideoTitle active />
                      </>
                    ) : (
                      <>
                        <PlayOutline20 className="cp-svg-auto-adjust-fill" />
                        <VideoTitle active />
                      </>
                    )}
                  </VideoScrollableMenuVideoListItem>
                ),
              )}
            </VideoScrollableMenuVideoList>
          </VideoScrollableMenuSectionWrapper>
        </VideoScrollableMenuWrapper>
      );

    default:
      return <></>;
  }
};

export const CpVideosLoading: React.FC<CpLoadingProps> = ({
  input,
  variantType,
  styles,
}) => {
  return (
    <CpSectionWrapper
      background={styles?.background as string}
      paddingY={styles?.paddingY as number | undefined}
      className="!cp-max-w-none"
    >
      <TitleAndDesc
        title={tryGetTextFromHTML(input?.title as string)}
        desc={tryGetTextFromHTML(input?.description as string) as string}
      />

      <Content variantType={variantType} input={input} />
    </CpSectionWrapper>
  );
};
